<template>
  <div id="supplier-list" class="container-fluid item-list pb-16 suppliers-wide" :class="{ 'loading-cursor': isBookingLoading }">
    <v-list-header :title="this.$t('supplier.suppliers')" icon="group"
      @open-filters-offcanvas="openOffCanvas('the-supplier-filters-offcanvas')" @emit-close-off-canvas="closeOffCanvas"
      @reload-suppliers="loadSuppliers">
      <template #input-search>
        <v-input-text-search id="supplier-search" v-model="mix" :placeholder="this.$t('general.shared.search')"
          autocomplete="off" class="w-100 me-24" input-class="h-auto py-4" input-group-text-class="py-4 px-16"
          type="text" @update:modelValue="mix = $event; search()" />
      </template>
    </v-list-header>

    <div class="items-content">
      <div class="filters-container d-flex mb-12">
        <v-filters v-if="!!filterOptions" v-model="filterOptions" @remove-filter="removeFilter" />
      </div>

      <!-- Nicer Filter Bar -->
      <div class="filters-bar d-flex align-items-end gap-2 mb-16 pb-12 border-bottom flex-wrap">
        <div class="form-outline mx-8" style="width: 130px;">
          <label class="label-xs mb-1">{{ $t('general.shared.startDate') }}</label>
          <Datepicker :format="'dd/MM/yyyy'" v-model="startDate" :enable-time-picker="false"
            :month-change-on-scroll="false" auto-apply class="datepicker-compact" @update:modelValue="updateDateRange" />
        </div>

        <div class="form-outline mx-8" style="width: 130px;">
          <label class="label-xs mb-1">{{ $t('general.shared.endDate') }}</label>
          <Datepicker :format="'dd/MM/yyyy'" v-model="endDate" :enable-time-picker="false"
            :month-change-on-scroll="false" auto-apply class="datepicker-compact" :min-date="startDate || undefined"
            @update:modelValue="updateDateRange" />
        </div>

        <div class="form-outline mx-8" style="width: 250px;">
          <label class="label-xs mb-1">{{ $t('general.shared.supplierList') }}</label>
          <v-select ref="supplierSelect" id="supplier-list-supplier-search"
            class="v-select-compact"
            v-model="supplierIds"
            :options="suppliersList" label="name" :reduce="(returnValue: any) => returnValue.id" multiple
            @option:selected="scrollToEnd">
            <template v-slot:selected-option="option">
              <span :title="option.name" style="font-size: 11px;">{{ option.name }}</span>
            </template>
            <template v-slot:no-options>
              <div v-if="suppliersList.length < 1" class="loader small">Loading...</div>
              <div v-else class="small">{{ $t('general.vueSelect.noOptions') }}</div>
            </template>
          </v-select>
        </div>

        <div class="ms-auto mx-8">
          <v-button class="btn-new-item d-flex align-items-center justify-content-center supplier-action-btn action-export-bookings"
            @click="exportBookingsToGoogleSheet()"
            :disabled="disableExportButton" :is-loading="disableExportButton">
            <v-icon icon="export" space="me-8" size="sm" class="text-white" />
            <span class="btn-text">{{ $t('general.button.exportBookings') }}</span>
          </v-button>
        </div>
      </div>

      <div class="list-table-shell mt-12">
        <div class="table-responsive table-for-suppliers">
        <table class="table fw-medium" style="table-layout: fixed;">
          <thead>
            <tr class="align-middle">
              <th style="width: 45%;">
                <v-button :class="{ 'sort-active': order.key === 'name' }"
                  class="btn-xs btn-icon btn btn-order" @click="toggleOrder('name')">
                  {{ this.$t('general.shared.name') }}
                  <v-icon :class="{ 'sort-icon-transition': order.key === 'name' && order.order === 'DESC' }"
                    icon="chevron-down" />
                </v-button>
              </th>
              <th style="width: 20%;">
                <v-button :class="{ 'sort-active': order.key === 'type' }"
                  class="btn-xs btn-icon btn btn-order" @click="toggleOrder('type')">
                  {{ this.$t('general.shared.type') }}
                  <v-icon :class="{ 'sort-icon-transition': order.key === 'type' && order.order === 'DESC' }"
                    icon="chevron-down" />
                </v-button>
              </th>
              <th style="width: 25%;">{{ this.$t('general.shared.location') }}</th>
              <th class="fit" />
            </tr>
          </thead>

          <tbody class="fw-medium">
            <tr v-if="!loaded">
              <td class="position-relative border-0 py-96" colspan="4">
                <v-loader />
              </td>
            </tr>
            <template v-else-if="!!suppliers && suppliers.length > 0">
              <template v-for="supplierItem in suppliers" :key="supplierItem.id">
                <tr class="align-middle" :class="{'active-row': selectedSupplierId === String(supplierItem.id) && showBookings}">
                  <td class="align-middle min-width-0">
                    <div class="d-flex align-items-center w-100 min-width-0">
                      <div class="image-container rounded-sm bg-primary me-12 flex-shrink-0">
                        {{ supplierItem.name.charAt(0) }}
                      </div>
                      <div class="d-flex align-items-center flex-grow-1 min-width-0">
                        <a class="py-4 text-truncate fw-bold text-dark flex-shrink-1 min-width-0" href="#" @click.prevent="showSupplierDetails(supplierItem)" :title="supplierItem.name">
                          {{ supplierItem.name }}
                        </a>
                        <span
                          class="status-icon-badge ms-8 flex-shrink-0"
                          :class="supplierItem.holdedId ? 'text-success' : 'text-danger'"
                          style="cursor: pointer;"
                          @mouseenter="showBadgeTooltip($event, supplierItem.holdedId ? 'Holded - click to sync' : 'No Holded ID - click to sync')"
                          @mouseleave="hideBadgeTooltip"
                          @click.stop="syncSupplierToHolded(supplierItem)"
                        >
                          <v-loader v-if="syncingHoldedId === supplierItem.id" size="xs" />
                          <v-icon v-else icon="pdf" size="xs" />
                        </span>
                        <span
                          class="status-icon-badge ms-4 flex-shrink-0"
                          :class="supplierItem.wiseId ? 'text-success' : 'text-danger'"
                          style="cursor: pointer;"
                          @mouseenter="showBadgeTooltip($event, supplierItem.wiseId ? 'Wise - click to sync' : 'No Wise ID - click to sync')"
                          @mouseleave="hideBadgeTooltip"
                          @click.stop="syncSupplierToWise(supplierItem)"
                        >
                          <v-loader v-if="syncingWiseId === supplierItem.id" size="xs" />
                          <v-icon v-else icon="euro" size="xs" />
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="text-truncate" :title="findConceptByKey('supplier.type', supplierItem.type)?.name || ''">
                    <span class="text-truncate d-inline-block align-middle" style="max-width: calc(100% - 28px);">
                      {{ findConceptByKey('supplier.type', supplierItem.type)?.name || '-' }}
                    </span>
                    <span
                      v-if="getAmenitiesCount(supplierItem.amenities) > 0"
                      class="amenities-count-badge ms-8 align-middle"
                      @mouseenter="showBadgeTooltip($event, getAllAmenities(supplierItem.amenities))"
                      @mouseleave="hideBadgeTooltip"
                    >
                      {{ getAmenitiesCount(supplierItem.amenities) }}
                    </span>
                  </td>

                  <td class="align-middle overflow-hidden" style="max-width: 0;">
                    <span class="d-block text-truncate pe-32" :title="getLocationName(supplierItem)">
                      <template v-if="!!getLocationName(supplierItem)">{{ getLocationName(supplierItem) }}</template>
                      <template v-else>-</template>
                    </span>
                  </td>
                  <td class="fit text-end align-middle supplier-actions-cell">
                    <div class="d-flex align-items-center justify-content-end flex-nowrap gap-8 py-1 supplier-actions">
                      <v-button v-if="selectedSupplierId === String(supplierItem.id) && showBookings"
                        class="btn-view supplier-row-action-btn" size="xs"
                        @click="exportBookingsToGoogleSheet(supplierItem.id)" :disabled="disableExportButton"
                        :is-loading="disableExportButton" :title="$t('general.toast.exportTooltip')">
                        <v-icon icon="export" />
                      </v-button>
                      <v-button
                        class="btn-view supplier-row-action-btn" size="xs"
                        :class="{'bg-warning border-warning': selectedSupplierId === String(supplierItem.id) && showBookings}"
                        @click="showSupplierBookings(supplierItem)"
                        :title="$t('general.toast.showSupplierBookings')">
                        <v-icon icon="bookmark" />
                      </v-button>
                      <v-button class="btn-view supplier-row-action-btn" size="xs"
                        @click="handleButtonClick(supplierItem)">
                        <v-icon icon="euro" />
                      </v-button>
                      <v-button class="btn-view rounded-pill bg-primary r-btn me-8" size="xs" variant="icon"
                        @click="$router.push({ name: 'supplierPortal.upcoming', query: { supplier_id: supplierItem.id } })">
                        <v-icon icon="calendar" />
                      </v-button>

                      <v-button class="btn-view supplier-row-action-btn" size="xs"
                        @click="showSupplierDetails(supplierItem)">
                        <v-icon icon="arrow-right" />
                      </v-button>
                    </div>
                  </td>
                </tr>

                <!-- Accordion Content -->
                <tr v-if="selectedSupplierId === String(supplierItem.id) && showBookings">
                  <td colspan="4" class="p-0 border-0">
                    <div class="accordion-box bg-white border-start border-end border-bottom pb-3 shadow-sm">
                      <div class="carousel-outer-wrapper position-relative px-5 py-3">
                        <Carousel class="carousel-container mb-2" :settings="getCarouselSettings()"
                          :breakpoints="carouselBreakpoints">
                          <Slide v-for="(data, index) in dateRange" :key="index">
                            <div class="date-slide-card p-1 text-center cursor-pointer border"
                              :class="{ 'active-date bg-light': index === selectedCardIndex }"
                              @click="selectDate(data.date.toISOString(), index)">
                              <div class="date-text">{{ formatDate(data.date) }}</div>
                              <div v-if="data.count == 0" class="no-bookings-text">No Bookings</div>
                              <div class="booking-count-text" v-else>
                                {{ data.count }} {{ data.count > 1 ? 'B' : 'B' }}
                              </div>
                            </div>
                          </Slide>
                          <template #addons>
                            <Navigation />
                          </template>
                        </Carousel>
                      </div>

                      <div v-if="isBookingLoading" class="text-center py-3">
                        <v-loader />
                      </div>

                      <div v-else class="table-responsive px-3 pb-3">
                        <table class="table table-sm border mb-0 compact-inner-table">
                          <thead>
                            <tr class="bg-light text-uppercase">
                              <th>{{ $t('tourBooking.caseName') }}</th>
                              <th>{{ $t('tourBooking.teamName') }}</th>
                              <th v-if="supplierItem.type == 'hotel'">{{ $t('tourBooking.roomType') }}</th>
                              <th v-if="['transport', 'transfer', 'bus'].includes(supplierItem.type)">
                                {{ $t('tourBooking.journeyRoute') }}
                              </th>
                              <th v-else-if="supplierItem.type !== 'hotel'">{{ $t('tourBooking.product') }}</th>
                              <th class="text-center">{{ $t('tourBooking.quantity') }}</th>
                              <th class="text-center">{{ $t('tourBooking.pax') }}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-if="bookingData[selectedDate]?.length > 0">
                              <tr v-for="booking in bookingData[selectedDate]" :key="booking.id">
                                <td class="py-1">
                                  <router-link target="_blank" :to="{ name: 'case.show', params: { caseId: booking.clientTourId } }">
                                    {{ booking.caseName }}
                                  </router-link>
                                </td>
                                <td class="py-1">{{ booking.collaboratorName }}</td>
                                <td class="py-1" v-if="supplierItem.type == 'hotel'">{{ booking.roomType }}</td>
                                <td v-else class="py-1">{{ booking.product }}</td>
                                <td class="py-1 text-center">{{ booking.quantity }}</td>
                                <td class="py-1 text-center">{{ booking.pax }}</td>
                              </tr>
                            </template>
                            <tr v-else>
                              <td colspan="10" class="text-center py-2 text-muted small">
                                {{ $t('tourBooking.noBookingsFound') }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </template>

            <template v-else>
              <tr>
                <td class="rounded-xs py-16 text-center" colspan="4">
                  {{ $t('supplier.noSuppliers') }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        </div>
      </div>

      <!-- Mobile Cards -->
      <div class="item-cards d-block d-md-none">
        <div v-if="!loaded" class="position-relative py-96">
          <v-loader />
        </div>
        <template v-else-if="!!suppliers && suppliers.length > 0">
          <div v-for="supplierItem in suppliers" :key="supplierItem.id" class="item-card border p-2 mb-2 text-sm shadow-none">
            <div class="d-flex align-items-center mb-2">
              <div class="image-container rounded-sm bg-primary me-2">
                {{ supplierItem.name.charAt(0) }}
              </div>
              <div class="flex-grow-1">
                <div class="fw-bold">{{ supplierItem.name }}</div>
                <div class="text-muted smaller">
                  {{ findConceptByKey('supplier.type', supplierItem.type).name }}
                  <span v-if="!!getLocationName(supplierItem)">• {{ getLocationName(supplierItem) }}</span>
                </div>
              </div>
            </div>
            <div class="d-flex justify-content-end gap-2 border-top pt-2">
              <v-button class="btn-view supplier-row-action-btn" size="xs" @click="showSupplierDetails(supplierItem)">
                <v-icon icon="arrow-right" />
              </v-button>
            </div>
          </div>
        </template>
      </div>

      <v-pagination v-if="!!suppliers && suppliers.length > 0" :meta="pagination.meta"
        :pagination-links="pagination.links" class="mt-4"
        @page-changed="changePage($event); handlePageChangedEvent($event)"
        @per-page-changed="perPage = $event; doSearch()" />
    </div>

    <the-supplier-filters-offcanvas v-if="offCanvasToShow === 'the-supplier-filters-offcanvas'"
      v-model:amenities="amenities" v-model:location="location" v-model:name="name" v-model:status="status"
      v-model:type="type" @closed="closeOffCanvas" @selected="doSearch" />
    <the-supplier-details-offcanvas
      v-if="offCanvasToShow === 'the-supplier-details-offcanvas' || offCanvasToShow === 'the-supplier-details-rates-off-canvas'"
      :is-archived="status === 'archived'" :supplier-data="supplier" @closed="closeOffCanvas(); cleanCurrentSupplier()"
      @reload-suppliers="loadSuppliers" />
    <the-supplier-details-rates-off-canvas v-if="offCanvasToShow === 'the-supplier-details-rates-off-canvas'"
      :supplier-id="supplier?.id || 0" @closed="closeRatesOffCanvas" @reload-supplier="reloadSupplier" />

    <Teleport to="body">
      <div
        v-if="badgeTooltip.visible"
        class="badge-hover-tooltip"
        :style="{ top: badgeTooltip.top + 'px', left: badgeTooltip.left + 'px' }"
      >
        {{ badgeTooltip.text }}
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref } from 'vue';
import { VInputTextSearch } from '@uniqoders/form';
import qs from 'qs';
import querystring from 'query-string';
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';
import VButton from '@/components/vendor/basic/button/VButton.vue';
import VLoader from '@/components/vendor/loader/VLoader.vue';
import api from '@/api';
import withTimeout from '@/helpers/timeOut';
import useOffCanvasUtils from '@/helpers/OffCanvasUtils';
import VListHeader from '@/components/shared/VListHeader.vue';
import VPagination from '@/components/vendor/basic/table/VPagination.vue';
import usePagination from '@/helpers/pagination';
import VFilters from '@/components/vendor/filters/VFilters.vue';
import TheSupplierFiltersOffcanvas from '@/components/supplier/TheSupplierFiltersOffcanvas.vue';
import TheSupplierDetailsOffcanvas from '@/components/supplier/TheSupplierDetailsOffcanvas.vue';
import TheSupplierDetailsRatesOffCanvas from '@/components/supplier/parts/TheSupplierDetailsRatesOffCanvas.vue';
import Supplier from '@/api/objects/Supplier';
import { findConceptByKey } from '@/helpers/ConceptHelper';
import useConcepts from '@/helpers/Concepts';
import Datepicker from '@vuepic/vue-datepicker';
import { Carousel, Navigation, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import { useToast } from "vue-toastification";
import { useI18n } from 'vue-i18n';
import vSelect from 'vue-select';
import { useAuthStore } from '@/stores/auth';
import { mapState } from 'pinia';

export default defineComponent({
  name: 'TheSuppliers',
  components: {
    TheSupplierDetailsOffcanvas,
    TheSupplierFiltersOffcanvas,
    TheSupplierDetailsRatesOffCanvas,
    VFilters,
    VPagination,
    VListHeader,
    VLoader,
    VButton,
    VInputTextSearch,
    VIcon,
    Datepicker,
    Carousel,
    Slide,
    vSelect,
    Navigation,
  },
  emits: ['reloadSupplier'],
  setup() {
    const startDate = ref<Date | null>(new Date());
    const endDate = ref<Date | null>(new Date(new Date().setDate(new Date().getDate() + 7)));
    const dateRange: Ref<{ date: Date; count: number; }[]> = ref([]);
    const showBookings = ref(false);
    const selectedSupplierId = ref('');
    const selectedCardIndex = ref(0);
    const selectedDate = ref('');
    const disableExportButton = ref(false);
    const toast = useToast();
    const { t } = useI18n();
    const supplierIds = ref<number[]>([]);
    const bookingData: Ref<Record<string, any[]>> = ref({});
    const isBookingLoading = ref(false);
    const syncingHoldedId = ref<number | string | null>(null);
    const syncingWiseId = ref<number | string | null>(null);

    const updateDateFormat = (dateString: string | Date) => {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
    };

    const updateDateRange = () => {
      const start = startDate.value || new Date();
      const end = endDate.value || new Date(new Date().setDate(start.getDate() + 30));
      const range = [];
      const currentDate = new Date(start);
      while (currentDate <= end) {
        range.push({ date: new Date(currentDate), count: 0 });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      dateRange.value = range;
      selectedDate.value = updateDateFormat(start);
      selectedCardIndex.value = 0;
    };

    const selectDate = (dateString: string, index: number) => {
      selectedDate.value = updateDateFormat(new Date(dateString));
      selectedCardIndex.value = index;
    };

    const loadHotelBookingData = async (supplierId: string) => {
      try {
        isBookingLoading.value = true;
        const params = {
          startDate: updateDateFormat(startDate.value || new Date()),
          endDate: updateDateFormat(endDate.value || new Date(new Date().setDate(new Date().getDate() + 30)))
        };
        const response = await api.supplier.getHotelBookingData(supplierId, params);
        const newBookingData: Record<string, any[]> = {};
        for (const date in response) {
          newBookingData[date] = response[date].items.map((item: any) => ({
            id: item.id,
            clientName: item.client_name || 'N/A',
            caseName: item.case_name || 'N/A',
            collaboratorName: item.collaborator_name || 'N/A',
            roomType: item.room_type,
            product: item.service || `${item.origin || 'N/A'} - ${item.destination || 'N/A'}`,
            quantity: item.quantity || '-',
            pax: item.pax,
            clientTourId: item.client_tour_id
          }));
          const rangeIndex = dateRange.value.findIndex(d => updateDateFormat(d.date) === date);
          if (rangeIndex !== -1) dateRange.value[rangeIndex].count = response[date].booking_count;
        }
        bookingData.value = newBookingData;
      } catch (e) {
        console.error(e);
      } finally {
        isBookingLoading.value = false;
      }
    };

    const exportBookingsToGoogleSheet = async (supplierId: string | number = '') => {
      disableExportButton.value = true;
      const today = new Date();
      const next30Days = new Date(new Date().setDate(today.getDate() + 30));
      const params = {
        startDate: startDate.value ? updateDateFormat(startDate.value) : updateDateFormat(today),
        endDate: endDate.value ? updateDateFormat(endDate.value) : updateDateFormat(next30Days),
        supplierIds: supplierId !== '' ? [Number(supplierId)] : supplierIds.value
      };
      try {
        const response = await api.supplier.exportToGoogleSheet(params);
        if (response.spreadsheetId) window.open('https://docs.google.com/spreadsheets/d/' + response.spreadsheetId, '_blank');
      } catch (error: any) {
        toast.error(error.response?.data?.message || t("general.shared.exportErrorToast"));
      } finally {
        disableExportButton.value = false;
      }
    };

    const formatDate = (date: Date) => {
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
    };

    return {
      startDate, endDate, dateRange, showBookings, supplierIds, updateDateRange,
      exportBookingsToGoogleSheet, formatDate, selectedCardIndex, selectedDate,
      selectDate, selectedSupplierId, findConceptByKey, ...useOffCanvasUtils(),
      ...usePagination(), ...useConcepts(), bookingData, loadHotelBookingData,
      disableExportButton, isBookingLoading, syncingHoldedId, syncingWiseId
    };
  },
  computed: {
    ...mapState(useAuthStore, ['agency']),
  },
  data() {
    return {
      carouselBreakpoints: {
        700: { itemsToShow: 5, snapAlign: 'start' },
        992: { itemsToShow: 5, snapAlign: 'start' },
        1200: { itemsToShow: 8, snapAlign: 'start' },
        1600: { itemsToShow: 8, snapAlign: 'start' }
      },
      suppliers: [] as Supplier[], supplier: null as null | Supplier,
      mix: '', name: '', type: '', amenities: [] as string[], location: '', status: 'active',
      suppliersList: [] as Supplier[],
      badgeTooltip: { visible: false, text: '', top: 0, left: 0 },
      filters: [
        { variable: 'status', key: 'status', labelAux: 'general.shared.filterStatus', conceptName: 'app.states', multiple: false, isConcept: true },
        { variable: 'mix', key: 'mix', labelAux: 'general.shared.filterSearch', multiple: false, isConcept: false },
        { variable: 'name', key: 'name', labelAux: 'general.shared.filterName', multiple: false, isConcept: false },
        { variable: 'type', key: 'type', labelAux: 'general.shared.filterType', conceptName: 'supplier.type', multiple: false, isConcept: true },
        { variable: 'amenities', key: 'amenities', labelAux: 'general.shared.filterAmenity', conceptName: 'supplier.amenities', multiple: true, isConcept: true },
        { variable: 'location', key: 'location', labelAux: 'general.shared.filterLocation', multiple: false, isConcept: false },
      ],
      order: { key: 'name', order: 'ASC' } as any,
      perPage: 15, page: 1, timers: { name: null as any }, loaded: false, filterOptions: [] as any[],
    };
  },
  async created() {
    await this.setQueryParametersAsFilters();
    await this.doSearch();
    await this.getAllSuppliers();
    this.updateDateRange();
  },
  watch: {
    supplierIds: {
      handler() { this.scrollToEnd(); },
      deep: true
    }
  },
  methods: {
    async getAllSuppliers() { this.suppliersList = await api.supplier.getAllSuppliers(); },
    getCarouselSettings() { return { itemsToShow: 10, snapAlign: 'center' }; },
    scrollToEnd() { /* Logic */ },
    search() {
      clearTimeout(this.timers.name);
      this.timers.name = withTimeout(async () => { await this.doSearch(); }, this.timers.name, 1000);
    },
    async setQueryParametersAsFilters() {
      const { query }: any = this.$route;
      const parsed = qs.parse(qs.stringify(query));
      if (parsed.filters) {
        this.filters.forEach(f => { if ((parsed.filters as any)[f.key]) this[f.variable] = (parsed.filters as any)[f.key]; });
      }
      if (parsed.order && typeof parsed.order === 'object') this.order = parsed.order;
      if (parsed.page) this.page = parseInt(parsed.page as string, 10);
      if (parsed.perPage) this.perPage = parseInt(parsed.perPage as string, 10);
      if (parsed.status) this.status = parsed.status as string;
    },
    async setFiltersAsQueryParameters() {
      const filters: any = {};
      this.filters.forEach(f => { if (this[f.variable]) filters[f.key] = this[f.variable]; });
      const queryObj = { filters, perPage: this.perPage, status: this.status, page: this.page, order: this.order.key ? this.order : undefined };
      this.setFilters();
      const queryStr = qs.stringify(queryObj, { encode: false });
      this.$router.push({ name: 'supplier.index', query: querystring.parse(queryStr) as any });
    },
    async doSearch() { await this.setFiltersAsQueryParameters(); await this.loadSuppliers(); },
    async loadSuppliers(page?: number) {
      try {
        this.loaded = false;
        const filters: any = {};
        this.filters.forEach(f => { if (this[f.variable]) filters[f.key] = this[f.variable]; });
        const query = { filters, perPage: this.perPage, status: this.status, page: page || this.page, order: this.order.key ? this.order : undefined };
        const response = await api.supplier.allPaginated(query);
        this.setResponse(response);
        this.suppliers = response.data;
      } catch (e: any) {
        this.$toast.error(e.response?.data?.message || "Error loading suppliers");
      } finally { this.loaded = true; }
    },
    async showSupplierDetails(supplierItem: Supplier) { this.supplier = supplierItem; this.openOffCanvas('the-supplier-details-offcanvas'); },
    async showSupplierBookings(supplierItem: Supplier) {
      if (this.selectedSupplierId === String(supplierItem.id) && this.showBookings) {
        this.showBookings = false; this.selectedSupplierId = ''; return;
      }
      this.selectedSupplierId = String(supplierItem.id);
      await this.loadHotelBookingData(this.selectedSupplierId);
      this.showBookings = true;
    },
    cleanCurrentSupplier() { this.supplier = null; },
    async handlePageChangedEvent(page: number) { await this.loadSuppliers(page); },
    toggleOrder(field: string) {
      this.order.order = (this.order.key === field && this.order.order === 'ASC') ? 'DESC' : 'ASC';
      this.order.key = field;
      this.search();
    },
    setFilters() {
      this.filterOptions = this.filters.flatMap(f => {
        const val = this[f.variable];
        if (!val || (Array.isArray(val) && val.length === 0)) return [];
        const options = Array.isArray(val) ? val : [val];
        return options.map(o => {
          const concept: any = f.isConcept ? findConceptByKey(f.conceptName, o) : null;
          const value = f.isConcept ? (concept?.key || o) : o;
          const label = this.$t(f.labelAux, { value: f.isConcept ? (concept?.name || o) : o });
          return { key: f.key, value, label };
        });
      });
    },
    removeFilter(filter: any) {
      this.filters.forEach(f => {
        if (f.key === filter.key) {
          if (Array.isArray(this[f.variable])) this[f.variable] = (this[f.variable] as any[]).filter(o => o !== filter.value);
          else this[f.variable] = '';
        }
      });
      if (filter.key === 'status') this.status = 'active';
      this.doSearch();
    },
    getAllAmenities(amenities: string): string {
      try {
        const parsed = JSON.parse(amenities);
        return parsed.map((a: string) => this.findConceptByKey('supplier.amenities', a)?.name).filter(Boolean).join(', ');
      } catch { return ''; }
    },
    getAmenitiesCount(amenities: string): number {
      if (!amenities) return 0;
      try {
        const parsed = JSON.parse(amenities);
        return Array.isArray(parsed) ? parsed.length : 0;
      } catch { return 0; }
    },
    showBadgeTooltip(event: MouseEvent, text: string) {
      if (!text) return;
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.badgeTooltip = {
        visible: true,
        text,
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      };
    },
    hideBadgeTooltip() {
      this.badgeTooltip.visible = false;
    },
    getLocationName(supplier: Supplier): string {
      if (supplier.locationName) return supplier.locationName;
      const parts = [supplier.contactCity, supplier.contactState, supplier.contactCountry].filter(Boolean);
      return parts.join(', ');
    },
    reloadSupplier() { this.loadSuppliers(); },
    closeRatesOffCanvas() { this.offCanvasToShow = 'the-supplier-details-offcanvas'; },
    async handleButtonClick(supplierItem: Supplier) {
      await this.showSupplierDetails(supplierItem);
      this.openOffCanvas('the-supplier-details-rates-off-canvas');
    },
    async syncSupplierToHolded(supplierItem: Supplier) {
      this.syncingHoldedId = supplierItem.id;
      try {
        const response = await api.supplier.syncToHolded(supplierItem.id);
        if (response.status) {
          supplierItem.holdedId = response.content.holded_id;
          this.$toast.success(response.message);
        } else {
          this.$toast.error(response.message);
        }
      } catch (e: any) {
        this.$toast.error(e.response?.data?.message || 'Failed to sync supplier to Holded');
      } finally {
        this.syncingHoldedId = null;
      }
    },
    async syncSupplierToWise(supplierItem: Supplier) {
      this.syncingWiseId = supplierItem.id;
      try {
        const response = await api.supplier.syncToWise(supplierItem.id);
        if (response.status) {
          supplierItem.wiseId = response.content.wise_id;
          this.$toast.success(response.message);
        } else {
          this.$toast.error(response.message);
        }
      } catch (e: any) {
        this.$toast.error(e.response?.data?.message || 'Failed to sync supplier to Wise');
      } finally {
        this.syncingWiseId = null;
      }
    }
  },
});
</script>

<style lang="scss" scoped>
.suppliers-wide {
  max-width: 1550px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1.25rem;
  padding-right: 1.25rem;
}

@media (min-width: 1400px) {
  .suppliers-wide {
    padding-left: 1.75rem;
    padding-right: 1.75rem;
  }
}

.loading-cursor { cursor: wait; }
.image-container {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
  border-radius: 6px !important;
  background-color: #fd0c6f !important;
}
.mx-8 { margin-left: 8px !important; margin-right: 8px !important; }
.date-slide-card {
  width: 100%;
  border-radius: 6px;
  .date-text { font-weight: 700; font-size: 11px; text-transform: uppercase; }
  .no-bookings-text { font-size: 10px; color: #999; }
  .booking-count-text { font-size: 11px; font-weight: bold; color: #57c68a; }
}
.accordion-box { background-color: #ffffff; border-radius: 0 0 6px 6px; }
.compact-inner-table { font-size: 12px !important; thead tr th { padding: 6px 8px !important; background-color: #f8f9fa !important; } }
.active-date { border-color: #fd0c6f !important; background-color: #fff8fb !important; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.cursor-pointer { cursor: pointer; }
.carousel-outer-wrapper {
  overflow: visible !important;
  padding: 0 50px !important;
  position: relative;
  :deep(.carousel__prev),
  :deep(.carousel__next) {
    background-color: #222422 !important;
    color: #ffffff !important;
    border-radius: 50% !important;
    width: 28px !important;
    height: 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    z-index: 50 !important;
    opacity: 1 !important;
    margin: 0 !important;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    &:hover { background-color: #171917 !important; }
    .carousel__icon { fill: #ffffff !important; width: 18px !important; height: 18px !important; }
  }
  :deep(.carousel__prev) { left: 0 !important; }
  :deep(.carousel__next) { right: 0 !important; }
}
.label-xs { font-size: 11px; font-weight: 700; text-transform: uppercase; color: #2D2D2D; }

.supplier-action-btn {
  min-height: 36px !important;
  border-radius: 6px !important;
  letter-spacing: 0.3px;
}

.action-export-bookings {
  background-color: #222422 !important;
  border-color: #222422 !important;
  color: #fff !important;
}

.action-export-bookings:hover {
  background-color: #171917 !important;
  border-color: #171917 !important;
}

.supplier-row-action-btn {
  border-radius: 6px !important;
}

.supplier-actions {
  min-height: 30px;
}

:deep(.btn:not(.rounded-circle)) {
  border-radius: 6px !important;
}

:deep(.btn-primary) {
  background-color: #fd0c6f !important;
  border-color: #fd0c6f !important;
  color: #fff !important;
}

:deep(.btn-primary:hover) {
  background-color: #dc095f !important;
  border-color: #dc095f !important;
}

.status-icon-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid currentColor;
  font-weight: 700;
  font-size: 9px;
  line-height: 1;
  cursor: help;
}

.status-icon-badge.text-success {
  background-color: rgba(25, 135, 84, 0.1);
}

.status-icon-badge.text-danger {
  background-color: rgba(220, 53, 69, 0.1);
}

.status-icon-badge .icon {
  font-size: 9px;
}

.amenities-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  border: 1px solid #adb5bd;
  background-color: #f1f3f5;
  color: #495057;
  font-weight: 700;
  font-size: 10px;
  line-height: 1;
  cursor: help;
}

.min-width-0 {
  min-width: 0;
}

:deep(.btn-order) {
  font-size: 12px !important;
  font-weight: 700 !important;
}
</style>

<style lang="scss">
.badge-hover-tooltip {
  position: fixed;
  transform: translate(-50%, -100%);
  z-index: 3000;
  background-color: #1a1d20;
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  pointer-events: none;
}

.badge-hover-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #1a1d20 transparent transparent transparent;
}

.datepicker-compact .dp__pointer { height: 36px; font-size: 13px; border-radius: 6px; }
.v-select-compact .vs__dropdown-toggle {
  min-height: 36px !important;
  width: 100%;
  border-radius: 6px !important;
  padding: 0 !important;
}
.v-select-compact .vs__selected-options { padding: 0 6px !important; display: flex; align-items: center; min-height: 36px !important; }
.v-select-compact .vs__selected {
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 11px;
  margin: 2px !important;
  padding: 0 6px !important;
  height: 24px;
  display: flex;
  align-items: center;
  color: #333;
}
.v-select-compact .vs__actions { padding: 0 6px !important; height: 36px !important; display: flex; align-items: center; }
.v-select-compact .vs__search { margin: 0 !important; padding: 0 7px !important; height: 34px !important; font-size: 12px; }
.v-select-compact .vs__deselect {
  fill: #000000 !important;
  opacity: 1 !important;
  margin-left: 4px !important;
  background: none !important;
  padding: 0 !important;
  svg { width: 10px !important; height: 10px !important; }
}
</style>
